import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from '../context/TranslationContext';
import { API_ENDPOINTS } from '../utils/config';
import { formatCurrency } from '../utils/currencyUtils';
import './CompletedRequestModal.css';

interface CompletedRequestModalProps {
  isOpen: boolean;
  onClose: () => void;
  requestId: string;
}

interface CompletedRequestDetail {
  _id: string;
  product_name: string;
  product_brand: string;
  product_description: string;
  quantity: number;
  unit: string;
  budget: number;
  currency: string;
  delivery_time: string;
  status: string;
  phase: string;
  created_at: string;
  completed_at: string;
  selected_suppliers: any[];
  selected_supplier: any;
  total_savings: number;
  savings_currency: string;
}

const CompletedRequestModal: React.FC<CompletedRequestModalProps> = ({ 
  isOpen, 
  onClose, 
  requestId 
}) => {
  const { token } = useAuth();
  const { t } = useTranslation();
  const [requestDetail, setRequestDetail] = useState<CompletedRequestDetail | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen && requestId) {
      fetchRequestDetail();
    }
  }, [isOpen, requestId]);

  const fetchRequestDetail = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_ENDPOINTS.REQUESTS}/${requestId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const responseData = await response.json();
        
        // Backend returns {data: {...}, success: true} format
        const requestData = responseData.data || responseData;
        setRequestDetail(requestData);
      } else {
        console.error('Failed to fetch request detail');
      }
    } catch (error) {
      console.error('Error fetching request detail:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (!isOpen) return null;

  return (
    <div className="completed-modal-overlay" onClick={onClose}>
      <div className="completed-modal-content" onClick={(e) => e.stopPropagation()}>
        <button className="completed-modal-close" onClick={onClose}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M18 6L6 18M6 6L18 18" stroke="#374151" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
        
        <div className="completed-modal-header">
          <div className="completed-header-icon">
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M9 12L11 14L15 10M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="#10B981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
          <h2>{t('common.completed_request_details')}</h2>
          <p className="completed-modal-subtitle">
            {t('common.request_successfully_completed')}
          </p>
        </div>

        {loading ? (
          <div className="completed-loading">
            <div className="loading-spinner"></div>
            <p>{t('common.loading')}...</p>
          </div>
        ) : requestDetail ? (
          <div className="completed-modal-body">
            {/* Request Information */}
            <div className="completed-section">
              <h3 className="section-title">{t('common.request_information')}</h3>
              <div className="info-grid">
                <div className="info-item">
                  <label>{t('common.product_name')}:</label>
                  <span>{requestDetail.product_name}</span>
                </div>
                <div className="info-item">
                  <label>{t('common.product_brand')}:</label>
                  <span>{requestDetail.product_brand || 'N/A'}</span>
                </div>
                <div className="info-item">
                  <label>{t('common.quantity')}:</label>
                  <span>{requestDetail.quantity} {requestDetail.unit}</span>
                </div>
                <div className="info-item">
                  <label>{t('common.delivery_time')}:</label>
                  <span>{requestDetail.delivery_time || '2-4 business days'}</span>
                </div>
                <div className="info-item">
                  <label>{t('common.created_at')}:</label>
                  <span>{formatDate(requestDetail.created_at)}</span>
                </div>
                <div className="info-item">
                  <label>{t('common.completed_at')}:</label>
                  <span>{formatDate(requestDetail.completed_at)}</span>
                </div>
              </div>
            </div>

            {/* Final Quote Information */}
            {requestDetail.selected_supplier && (
              <div className="completed-section">
                <h3 className="section-title">{t('common.final_quote')}</h3>
                <div className="quote-card">
                  <div className="quote-header">
                    <h4>{requestDetail.selected_supplier.supplier_name}</h4>
                    <span className="quote-status completed">{t('common.selected')}</span>
                  </div>
                  <div className="quote-details">
                    <div className="quote-item">
                      <label>{t('common.price')}:</label>
                      <span className="price">{formatCurrency(requestDetail.selected_supplier.unit_price, requestDetail.selected_supplier.currency)}</span>
                    </div>
                    <div className="quote-item">
                      <label>{t('common.total_price')}:</label>
                      <span className="price total">{formatCurrency(requestDetail.selected_supplier.unit_price * requestDetail.quantity, requestDetail.selected_supplier.currency)}</span>
                    </div>
                    <div className="quote-item">
                      <label>{t('common.delivery_time')}:</label>
                      <span>{requestDetail.selected_supplier.delivery_time || '2-4 business days'}</span>
                    </div>
                    {requestDetail.selected_supplier.notes && (
                      <div className="quote-item">
                        <label>{t('common.notes')}:</label>
                        <span>{requestDetail.selected_supplier.notes}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Savings Information */}
            {requestDetail.total_savings && requestDetail.total_savings > 0 && (
              <div className="completed-section">
                <h3 className="section-title">{t('common.savings_achieved')}</h3>
                <div className="savings-card">
                  <div className="savings-item">
                    <label>{t('common.original_budget')}:</label>
                    <span className="original-budget">{formatCurrency(requestDetail.budget, requestDetail.currency)}</span>
                  </div>
                  <div className="savings-item">
                    <label>{t('common.final_price')}:</label>
                    <span className="final-price">{formatCurrency(requestDetail.selected_supplier.unit_price * requestDetail.quantity, requestDetail.selected_supplier.currency)}</span>
                  </div>
                  <div className="savings-item savings-amount">
                    <label>{t('common.savings_amount')}:</label>
                    <span className="savings-value positive">{formatCurrency(requestDetail.budget - (requestDetail.selected_supplier.unit_price * requestDetail.quantity), requestDetail.currency)}</span>
                  </div>
                  <div className="savings-item savings-percentage">
                    <label>{t('common.savings_percentage')}:</label>
                    <span className="savings-percent positive">{(((requestDetail.budget - (requestDetail.selected_supplier.unit_price * requestDetail.quantity)) / requestDetail.budget) * 100).toFixed(1)}%</span>
                  </div>
                </div>
              </div>
            )}

          </div>
        ) : (
          <div className="completed-error">
            <p>{t('common.error_loading_request')}</p>
          </div>
        )}

        <div className="completed-modal-footer">
          <button className="completed-modal-btn secondary" onClick={() => window.open('/invoice_air_purifier_tax.pdf', '_blank')}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M21 15V19C21 19.5304 20.7893 20.0391 20.4142 20.4142C20.0391 20.7893 19.5304 21 19 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M7 10L12 15L17 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M12 15V3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            Download Invoice
          </button>
          <button className="completed-modal-btn primary" onClick={onClose}>
            {t('common.close')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CompletedRequestModal;
